/**
 * Scout v0 — run history list (P17).
 *
 * Every launch records a run (identity + criteria snapshot + stage scorecard);
 * this is the read surface: newest first, status badge, criteria version,
 * launch/completion times. Rows link to the run detail (funnel + recorded
 * criteria). The list polls while any run is still in flight.
 */

import { Link } from '@tanstack/react-router';
import { useScoutRuns } from '@/lib/scout-queries';
import { EmptyState, ErrorBanner, LoadingSpinner } from '@/components/ui';
import { getErrorMessage } from '@/lib/queries';
import { RunStatusBadge } from './RunStatusBadge';

function formatTimestamp(iso: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RunsList() {
  const { data: runs, isLoading, error, refetch } = useScoutRuns();

  if (error) {
    return (
      <section className="card" aria-labelledby="runs-heading">
        <h2 id="runs-heading" className="text-lg font-semibold text-text mb-3">
          Runs
        </h2>
        <ErrorBanner message={getErrorMessage(error)} retryable={error.retryable} onRetry={() => refetch()} />
      </section>
    );
  }

  return (
    <section className="card" aria-labelledby="runs-heading">
      <h2 id="runs-heading" className="text-lg font-semibold text-text mb-3">
        Runs
      </h2>

      {isLoading ? (
        <LoadingSpinner className="py-6" />
      ) : !runs || runs.length === 0 ? (
        <EmptyState
          title="No runs yet"
          description="Launch a run from the Criteria Composer to see its status here."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-border">
                <th className="py-2 pr-3 font-medium">Run</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Criteria version</th>
                <th className="py-2 pr-3 font-medium">Launched</th>
                <th className="py-2 pr-3 font-medium">Completed</th>
                <th className="py-2 pr-3 font-medium">QA</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.runId} className="border-b border-border/50">
                  <td className="py-2 pr-3">
                    <Link
                      to="/scout/runs/$runId"
                      params={{ runId: run.runId }}
                      className="font-mono text-xs text-secondary hover:underline"
                    >
                      {run.runId}
                    </Link>
                  </td>
                  <td className="py-2 pr-3">
                    <RunStatusBadge status={run.status} />
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-muted">{run.criteriaVersion || '-'}</td>
                  <td className="py-2 pr-3 text-muted">{formatTimestamp(run.createdAt)}</td>
                  <td className="py-2 pr-3 text-muted">{formatTimestamp(run.completedAt)}</td>
                  <td className="py-2 pr-3">
                    {run.status === 'completed' && (
                      <Link
                        to="/scout/qa/$runId"
                        params={{ runId: run.runId }}
                        className="text-xs text-secondary hover:underline whitespace-nowrap"
                      >
                        Review QA
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
